import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  LinearProgress,
  Button
} from '@mui/material';
import withoutQR from "../../../assests/withoutQR1.png";
import jsPDF from 'jspdf';
import centerqrlogo from "../../../assests/grouplogo1.svg"
import './BulkQRCodeGenerator.css';

const BulkQRCodeGenerator = ({ open, onClose, uids, onPDFDownloadComplete, pdfDownloading }) => {
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [qrCodesLoaded, setQrCodesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState(0);
  const [qrCodeImages, setQrCodeImages] = useState({});
  const containerRef = useRef(null);

  // Card dimensions for A4 page (4 per page)
  const CARD_WIDTH = 95; // mm - for 2 columns
  const CARD_HEIGHT = 140; // mm - for 2 rows
  const CARDS_PER_PAGE = 4;

  // Original card dimensions in pixels (from your design)
  const CARD_WIDTH_PX = 378;
  const CARD_HEIGHT_PX = 600;

  const generateQRCodeUrl = (uid) => {
    const qrData = `https://www.daalchini.co.in/uuid?value=${uid}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}&format=png&margin=10`;
  };

  useEffect(() => {
    if (open && uids.length > 0) {
      setQrCodesLoaded(false);
      setLoadedImages(0);
      setQrCodeImages({});
      preloadAllImages();
    }
  }, [open, uids]);

  useEffect(() => {
    if (open && uids.length > 0 && pdfDownloading && qrCodesLoaded && !isDownloading) {
      setTimeout(() => {
        downloadOptimizedPDF();
      }, 1000);
    }
  }, [open, uids, pdfDownloading, qrCodesLoaded, isDownloading]);

  const preloadAllImages = async () => {
    const imagePromises = uids.map(async (uidObj) => {
      const uid = uidObj.uid || uidObj;
      
      try {
        // Preload QR code image
        const qrImageBlob = await loadImageAsBlob(generateQRCodeUrl(uid));
        // Preload background image
        const bgImageBlob = await loadImageAsBlob(withoutQR);
        // Preload logo image
        const logoImageBlob = await loadImageAsBlob(centerqrlogo);
        
        return {
          uid,
          qrImage: URL.createObjectURL(qrImageBlob),
          bgImage: URL.createObjectURL(bgImageBlob),
          logoImage: URL.createObjectURL(logoImageBlob)
        };
      } catch (error) {
        console.error(`Failed to load images for UID ${uid}:`, error);
        return {
          uid,
          qrImage: generateQRCodeUrl(uid),
          bgImage: withoutQR,
          logoImage: centerqrlogo
        };
      }
    });

    try {
      const loadedImages = await Promise.all(imagePromises);
      const imagesMap = {};
      loadedImages.forEach(img => {
        imagesMap[img.uid] = img;
      });
      setQrCodeImages(imagesMap);
      setQrCodesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      setQrCodesLoaded(true);
    }
  };

  const loadImageAsBlob = (url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(new Error(`Failed to load image: ${url}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Network error loading image: ${url}`));
      xhr.send();
    });
  };

  const createCardCanvas = async (uid, qrImageUrl, bgImageUrl, logoImageUrl) => {
    return new Promise(async (resolve) => {
      // Create canvas with exact dimensions
      const canvas = document.createElement('canvas');
      const scale = 3; // Higher resolution for better print quality
      canvas.width = CARD_WIDTH_PX * scale;
      canvas.height = CARD_HEIGHT_PX * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      try {
        // Load background image
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        
        bgImg.onload = () => {
          // Draw exact background image
          ctx.drawImage(bgImg, 0, 0, CARD_WIDTH_PX, CARD_HEIGHT_PX);

          // Load QR code
          const qrImg = new Image();
          qrImg.crossOrigin = "anonymous";
          
          qrImg.onload = () => {
            // Perfect center position for QR code - UPPER POSITION
            const qrSize = 240; // pixels - BIGGER size for better visibility
            const qrX = (CARD_WIDTH_PX - qrSize) / 2; // Perfect horizontal center
            const qrY = (CARD_HEIGHT_PX - qrSize) / 2 - 90; // MOVED UP by 90 pixels

            // White background for QR code with shadow
            ctx.fillStyle = 'transparent';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 6;
            ctx.fillRect(qrX - 20, qrY - 20, qrSize + 35, qrSize + 35);
            
            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            
            // Draw QR code
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

            // Load and draw logo in center of QR code
            const logoImg = new Image();
            logoImg.crossOrigin = "anonymous";
            
            logoImg.onload = () => {
              // Logo dimensions - QR code ke andar center mein
              const logoSize = 40; // Normal size logo
              const logoX = qrX + (qrSize - logoSize) / 2;
              const logoY = qrY + (qrSize - logoSize) / 2;
              
              // White circular background for logo
              ctx.fillStyle = 'transparent';
              ctx.beginPath();
              ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2 + 2, 0, 2 * Math.PI);
              ctx.fill();
              
              // Draw logo
              ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

              // Draw UID text - perfectly centered below QR
              const uidText = `UID: ${uid}`;
              ctx.font = 'bold 20px Arial';
              ctx.fillStyle = '#333333';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              // Position UID exactly below QR code with margin
              const textY = qrY + qrSize + 40; // ✅ 35 se 50 kar diya - aur neeche
              ctx.fillText(uidText, CARD_WIDTH_PX / 2, textY);

              resolve(canvas);
            };

            logoImg.onerror = () => {
              console.error('Logo failed to load');
              // Agar logo load na ho, toh bhi UID draw karo
              const uidText = `UID: ${uid}`;
              ctx.font = 'bold 20px Arial';
              ctx.fillStyle = '#333333';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              const textY = qrY + qrSize + 50; // ✅ Yahan bhi 50 kar do
              ctx.fillText(uidText, CARD_WIDTH_PX / 2, textY);
              
              resolve(canvas);
            };

            logoImg.src = logoImageUrl;
          };

          qrImg.onerror = () => {
            // If QR fails, still draw background
            console.error('QR code failed to load for UID:', uid);
            resolve(canvas);
          };

          qrImg.src = qrImageUrl;
        };

        bgImg.onerror = () => {
          // If background fails, create fallback
          console.error('Background image failed to load');
        
          ctx.fillRect(0, 0, CARD_WIDTH_PX, CARD_HEIGHT_PX);
          
          ctx.font = 'bold 24px Arial';
        
          ctx.textAlign = 'center';
          ctx.fillText('Daalchini', CARD_WIDTH_PX / 2, CARD_HEIGHT_PX / 2);
          
          resolve(canvas);
        };

        bgImg.src = bgImageUrl;
      } catch (error) {
        console.error('Error creating card canvas:', error);
        resolve(canvas);
      }
    });
  };

  const downloadOptimizedPDF = async () => {
    if (uids.length === 0 || isDownloading) return;
    
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const pdf = new jsPDF({ 
        orientation: 'portrait', 
        unit: 'mm', 
        format: 'a4' 
      });

      const totalPages = Math.ceil(uids.length / CARDS_PER_PAGE);
      let processedCards = 0;

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        // Calculate cards for this page
        const startIndex = page * CARDS_PER_PAGE;
        const endIndex = Math.min(startIndex + CARDS_PER_PAGE, uids.length);
        const pageCards = uids.slice(startIndex, endIndex);

        // Process all cards for this page
        const cardPromises = pageCards.map(async (uidObj, cardIndex) => {
          const uid = uidObj.uid || uidObj;
          const images = qrCodeImages[uid] || {
            qrImage: generateQRCodeUrl(uid),
            bgImage: withoutQR,
            logoImage: centerqrlogo
          };

          const canvas = await createCardCanvas(uid, images.qrImage, images.bgImage, images.logoImage);
          return { canvas, cardIndex, uid };
        });

        const cards = await Promise.all(cardPromises);

        // Position cards on A4 page in 2x2 grid with perfect alignment
        cards.forEach(({ canvas, cardIndex }) => {
          const row = Math.floor(cardIndex / 2);
          const col = cardIndex % 2;
          
          // Calculate positions with perfect margins for A4
          const pageWidth = 210; // A4 width in mm
          const pageHeight = 297; // A4 height in mm
          
          const horizontalMargin = (pageWidth - (2 * CARD_WIDTH)) / 3;
          const verticalMargin = (pageHeight - (2 * CARD_HEIGHT)) / 3;
          
          const x = col * (CARD_WIDTH + horizontalMargin) + horizontalMargin;
          const y = row * (CARD_HEIGHT + verticalMargin) + verticalMargin;

          // Convert canvas to image and add to PDF
          const imgData = canvas.toDataURL('image/png', 1.0);
          pdf.addImage(imgData, 'PNG', x, y, CARD_WIDTH, CARD_HEIGHT);
        });

        processedCards += pageCards.length;
        const progress = Math.round((processedCards / uids.length) * 100);
        setDownloadProgress(progress);

        // Small delay for better performance
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Save PDF
      pdf.save(`daalchini_qr_cards_${new Date().toISOString().split('T')[0]}.pdf`);
      
      console.log('PDF download completed successfully');

    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      if (onPDFDownloadComplete) onPDFDownloadComplete();
    }
  };

  const handleManualDownload = () => {
    if (!isDownloading && qrCodesLoaded) {
      downloadOptimizedPDF();
    }
  };

  const handleClose = () => {
    if (!isDownloading) onClose();
  };

  // Clean up object URLs
  useEffect(() => {
    return () => {
      Object.values(qrCodeImages).forEach(imageObj => {
        if (imageObj.qrImage && imageObj.qrImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.qrImage);
        }
        if (imageObj.bgImage && imageObj.bgImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.bgImage);
        }
        if (imageObj.logoImage && imageObj.logoImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.logoImage);
        }
      });
    };
  }, [qrCodeImages]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ style: { borderRadius: '20px', overflow: 'hidden', minHeight: '500px', maxWidth: '1200px' } }}>
      <DialogContent sx={{ p: 3 }}>
        <Box className="uid-bulk-dashboard-container" ref={containerRef}>
          <Box className="uid-bulk-dashboard-header">
            <Typography variant="h5" className="uid-bulk-dashboard-title">
              {isDownloading ? `Generating PDF... ${downloadProgress}%` : `QR Codes for ${uids.length} UIDs`}
            </Typography>
          </Box>

          {isDownloading && (
            <Box className="uid-bulk-dashboard-download-progress">
              <Typography variant="body2" className="uid-bulk-dashboard-progress-text">
                Creating PDF with {uids.length} QR codes... {downloadProgress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={downloadProgress} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5, 
                  backgroundColor: '#f0f0f0', 
                  '& .MuiLinearProgress-bar': { 
                    backgroundColor: '#FF8B36', 
                    borderRadius: 5 
                  } 
                }} 
              />
              <Typography variant="body2" className="uid-bulk-dashboard-progress-note">
                Organizing {uids.length} cards into A4 pages (4 per page)...
              </Typography>
            </Box>
          )}

          {!qrCodesLoaded && !isDownloading && (
            <Box className="uid-bulk-dashboard-loading-message">
              <Typography variant="body1" className="uid-bulk-dashboard-loading-message-text">
                Loading QR codes... {Object.keys(qrCodeImages).length}/{uids.length} loaded
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {qrCodesLoaded && !isDownloading && !pdfDownloading && (
            <Box className="uid-bulk-dashboard-manual-download">
              <Button 
                variant="contained" 
                onClick={handleManualDownload}
                sx={{ 
                  backgroundColor: '#FF8B36', 
                  '&:hover': { backgroundColor: '#FF6B00' } 
                }}
              >
                Download PDF Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                Ready to download {uids.length} QR codes ({Math.ceil(uids.length / 4)} A4 pages)
              </Typography>
            </Box>
          )}

          {/* Preview Grid - Showing exact cards */}
          {!isDownloading && (
            <Box className="uid-bulk-dashboard-codes-grid">
              {uids.map((uidObj, index) => {
                const uid = uidObj.uid || uidObj;
                const images = qrCodeImages[uid] || {
                  qrImage: generateQRCodeUrl(uid),
                  bgImage: withoutQR,
                  logoImage: centerqrlogo
                };

                return (
                  <Box 
                    key={`${uid}-${index}`}
                    className="uid-bulk-dashboard-code-item"
                  >
                    <Box className="uid-bulk-dashboard-poster-container">
                      <img 
                        src={images.bgImage} 
                        alt="Poster Background" 
                        className="uid-bulk-dashboard-poster-bg"
                        crossOrigin="anonymous"
                      />
                      <Box className="uid-bulk-dashboard-qr-overlay">
                        <img 
                          src={images.qrImage} 
                          alt={`QR Code for ${uid}`}
                          className="uid-bulk-dashboard-qr-image"
                          crossOrigin="anonymous"
                        />
                        {/* Logo in center of QR code */}
                        <Box className="uid-bulk-dashboard-logo-overlay">
                          <img 
                            src={images.logoImage} 
                            alt="Logo"
                            className="uid-bulk-dashboard-logo-image"
                            crossOrigin="anonymous"
                          />
                        </Box>
                      </Box>
                      <Box className="uid-bulk-dashboard-uid-overlay">
                        <Typography variant="h6" className="uid-bulk-dashboard-uid-text" >
                          UID: {uid}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkQRCodeGenerator;